/**
 * A single relationship group row (header + optional expansion) for ViewAllLinksModal.
 *
 * Extracted from ViewAllLinksModal.tsx (Sub-PR 6.4).
 */

import { useTranslation } from 'react-i18next'
import {
  ArrowLeft,
  ArrowRight,
  ChevronDown,
  ChevronRight,
  Edit,
  HelpCircle,
  Trash2,
} from 'lucide-react'
import type { EndpointRelationship } from '../../../types/case-container'
import type { RelationshipGroup } from './types'
import { formatType, getStatusColour } from './helpers'

const TARGET_PREVIEW_MAX = 3

interface RelationshipGroupRowProps {
  group: RelationshipGroup
  isExpanded: boolean
  onToggle: () => void
  onEdit: (rel: EndpointRelationship) => void
  onDelete: (id: string) => void
  deleting: boolean
}

function getTargetPreview(
  group: RelationshipGroup,
  unknownLabel: string,
  moreLabel: string
): string {
  const hostnames = group.targetEndpoints
    .map((ep) => ep?.hostname ?? unknownLabel)
    .slice(0, TARGET_PREVIEW_MAX)
  const remaining = group.targetEndpoints.length - TARGET_PREVIEW_MAX
  if (remaining > 0) return `${hostnames.join(', ')}, +${String(remaining)} ${moreLabel}`
  return hostnames.join(', ')
}

interface DirectionIndicatorProps {
  isExpandable: boolean
  isExpanded: boolean
  isOutgoing: boolean
}

function DirectionIndicator({
  isExpandable,
  isExpanded,
  isOutgoing,
}: DirectionIndicatorProps): React.ReactElement {
  if (isExpandable) {
    return (
      <button className="flex h-8 w-8 items-center justify-center rounded-full bg-slate-100 dark:bg-slate-800">
        {isExpanded ? (
          <ChevronDown className="h-4 w-4 text-slate-600 dark:text-slate-400" />
        ) : (
          <ChevronRight className="h-4 w-4 text-slate-600 dark:text-slate-400" />
        )}
      </button>
    )
  }
  if (isOutgoing) {
    return (
      <div className="flex h-8 w-8 items-center justify-center rounded-full bg-blue-100 dark:bg-blue-950/50">
        <ArrowRight className="h-4 w-4 text-blue-600 dark:text-blue-400" />
      </div>
    )
  }
  return (
    <div className="flex h-8 w-8 items-center justify-center rounded-full bg-green-100 dark:bg-green-950/50">
      <ArrowLeft className="h-4 w-4 text-green-600 dark:text-green-400" />
    </div>
  )
}

interface SingleActionsProps {
  rel: EndpointRelationship
  onEdit: (rel: EndpointRelationship) => void
  onDelete: (id: string) => void
  deleting: boolean
}

function SingleActions({
  rel,
  onEdit,
  onDelete,
  deleting,
}: SingleActionsProps): React.ReactElement {
  const { t } = useTranslation('cases')
  return (
    <div className="flex items-center gap-1">
      <button
        onClick={(e) => {
          e.stopPropagation()
          onEdit(rel)
        }}
        className="rounded p-1.5 text-slate-400 hover:bg-slate-100 hover:text-fuchsia-600 dark:hover:bg-slate-800 dark:hover:text-fuchsia-400"
        title={t('actions.edit')}
      >
        <Edit className="h-4 w-4" />
      </button>
      <button
        onClick={(e) => {
          e.stopPropagation()
          if (confirm(t('infrastructure.viewAllLinks.confirmDelete'))) onDelete(rel.id)
        }}
        disabled={deleting}
        className="rounded p-1.5 text-slate-400 hover:bg-slate-100 hover:text-red-600 disabled:opacity-50 dark:hover:bg-slate-800 dark:hover:text-red-400"
        title={t('actions.delete')}
      >
        <Trash2 className="h-4 w-4" />
      </button>
    </div>
  )
}

function MultiDirectionBadge({ isOutgoing }: { isOutgoing: boolean }): React.ReactElement {
  const { t } = useTranslation('cases')
  return (
    <div className="flex-shrink-0">
      {isOutgoing ? (
        <span className="inline-flex items-center gap-1 rounded-full bg-blue-100 px-2 py-1 text-xs text-blue-700 dark:bg-blue-950/50 dark:text-blue-400">
          <ArrowRight className="h-3 w-3" />
          {t('infrastructure.viewAllLinks.out')}
        </span>
      ) : (
        <span className="inline-flex items-center gap-1 rounded-full bg-green-100 px-2 py-1 text-xs text-green-700 dark:bg-green-950/50 dark:text-green-400">
          <ArrowLeft className="h-3 w-3" />
          {t('infrastructure.viewAllLinks.in')}
        </span>
      )}
    </div>
  )
}

interface ExpandedRowProps {
  rel: EndpointRelationship
  targetHostname: string
  isLast: boolean
  onEdit: (rel: EndpointRelationship) => void
  onDelete: (id: string) => void
  deleting: boolean
}

function ExpandedRow({
  rel,
  targetHostname,
  isLast,
  onEdit,
  onDelete,
  deleting,
}: ExpandedRowProps): React.ReactElement {
  const { t } = useTranslation('cases')
  const relIsOpenQuestion = rel.status === 'open-question'
  const borderClass = isLast ? '' : 'border-b border-slate-100 dark:border-slate-800'

  return (
    <div
      className={`flex items-start gap-3 px-3 py-2.5 ${borderClass} hover:bg-slate-50 dark:hover:bg-slate-800/30`}
    >
      <div className="w-8 flex-shrink-0" />
      <div className="min-w-0 flex-1">
        <div className="flex items-center gap-2">
          <ArrowRight className="h-3 w-3 flex-shrink-0 text-slate-400" />
          <span className="text-sm font-medium text-slate-800 dark:text-slate-200">
            {targetHostname}
          </span>
          {rel.label && (
            <span className="text-xs text-slate-500 dark:text-slate-400">&middot; {rel.label}</span>
          )}
        </div>
        {relIsOpenQuestion && rel.investigationNotes && (
          <div className="mt-2 ml-5 rounded border border-amber-200 bg-amber-100/50 p-2 dark:border-amber-800 dark:bg-amber-900/30">
            <div className="flex items-start gap-2">
              <HelpCircle className="mt-0.5 h-3.5 w-3.5 flex-shrink-0 text-amber-600 dark:text-amber-400" />
              <p className="text-xs text-amber-800 dark:text-amber-300">{rel.investigationNotes}</p>
            </div>
          </div>
        )}
        {relIsOpenQuestion && !rel.investigationNotes && (
          <p className="mt-1 ml-5 text-xs text-amber-600 italic dark:text-amber-500">
            {t('infrastructure.viewAllLinks.noQuestionText')}
          </p>
        )}
      </div>
      <div className="flex flex-shrink-0 items-center gap-1">
        <button
          onClick={() => {
            onEdit(rel)
          }}
          className="rounded p-1 text-slate-400 hover:bg-slate-100 hover:text-fuchsia-600 dark:hover:bg-slate-800 dark:hover:text-fuchsia-400"
          title={t('actions.edit')}
        >
          <Edit className="h-3.5 w-3.5" />
        </button>
        <button
          onClick={() => {
            if (confirm(t('infrastructure.viewAllLinks.confirmDelete'))) onDelete(rel.id)
          }}
          disabled={deleting}
          className="rounded p-1 text-slate-400 hover:bg-slate-100 hover:text-red-600 disabled:opacity-50 dark:hover:bg-slate-800 dark:hover:text-red-400"
          title={t('actions.delete')}
        >
          <Trash2 className="h-3.5 w-3.5" />
        </button>
      </div>
    </div>
  )
}

interface GroupSummaryProps {
  group: RelationshipGroup
  isExpanded: boolean
  isSingle: boolean
  firstTarget: { hostname: string } | undefined
  isOpenQuestion: boolean
}

function GroupSummary({
  group,
  isExpanded,
  isSingle,
  firstTarget,
  isOpenQuestion,
}: GroupSummaryProps): React.ReactElement {
  const { t } = useTranslation('cases')
  const unknownLabel = t('infrastructure.viewAllLinks.unknown')
  const moreLabel = t('infrastructure.bulkCreate.more')

  return (
    <div className="min-w-0 flex-1">
      <div className="flex items-center gap-2 text-sm">
        <span className="font-medium text-slate-900 dark:text-slate-100">
          {group.sourceEndpoint?.hostname ?? unknownLabel}
        </span>
        <ArrowRight className="h-3.5 w-3.5 flex-shrink-0 text-slate-400" />
        {isSingle ? (
          <span className="truncate font-medium text-slate-900 dark:text-slate-100">
            {firstTarget?.hostname ?? unknownLabel}
          </span>
        ) : (
          <span className="truncate text-slate-600 dark:text-slate-400">
            <span className="font-semibold text-fuchsia-600 dark:text-fuchsia-400">
              {group.relationships.length}
            </span>{' '}
            {t('infrastructure.viewAllLinks.endpoints', { count: group.relationships.length })}
          </span>
        )}
      </div>
      <div className="mt-1 flex items-center gap-2">
        <span className="text-xs text-slate-500 dark:text-slate-400">
          {formatType(group.connectionType)}
        </span>
        <span
          className={`inline-flex items-center rounded px-1.5 py-0.5 text-xs font-medium ${getStatusColour(group.status)}`}
        >
          {isOpenQuestion && <HelpCircle className="mr-1 h-3 w-3" />}
          {group.status.replaceAll('-', ' ')}
        </span>
        {!isSingle && !isExpanded && (
          <span className="ml-2 truncate text-xs text-slate-400 dark:text-slate-500">
            {getTargetPreview(group, unknownLabel, moreLabel)}
          </span>
        )}
      </div>
    </div>
  )
}

function SingleOpenQuestionNote({ note }: { note: string }): React.ReactElement {
  return (
    <div className="ml-11 px-3 pt-0 pb-3">
      <div className="rounded-md border border-amber-200 bg-amber-100/50 p-2.5 dark:border-amber-800 dark:bg-amber-900/30">
        <div className="flex items-start gap-2">
          <HelpCircle className="mt-0.5 h-4 w-4 flex-shrink-0 text-amber-600 dark:text-amber-400" />
          <p className="text-sm text-amber-800 dark:text-amber-300">{note}</p>
        </div>
      </div>
    </div>
  )
}

interface GroupHeaderRowProps {
  group: RelationshipGroup
  isExpanded: boolean
  isSingle: boolean
  firstRelationship: EndpointRelationship | undefined
  firstTarget: { hostname: string } | undefined
  isOpenQuestion: boolean
  onToggle: () => void
  onEdit: (rel: EndpointRelationship) => void
  onDelete: (id: string) => void
  deleting: boolean
}

function GroupHeaderRow({
  group,
  isExpanded,
  isSingle,
  firstRelationship,
  firstTarget,
  isOpenQuestion,
  onToggle,
  onEdit,
  onDelete,
  deleting,
}: GroupHeaderRowProps): React.ReactElement {
  const headerExtra = isSingle ? '' : 'cursor-pointer hover:bg-slate-50 dark:hover:bg-slate-800/50'
  return (
    <div
      className={`flex items-center gap-3 p-3 ${headerExtra}`}
      onClick={() => {
        if (!isSingle) onToggle()
      }}
    >
      <div className="flex-shrink-0">
        <DirectionIndicator
          isExpandable={!isSingle}
          isExpanded={isExpanded}
          isOutgoing={group.isOutgoing}
        />
      </div>
      <GroupSummary
        group={group}
        isExpanded={isExpanded}
        isSingle={isSingle}
        firstTarget={firstTarget}
        isOpenQuestion={isOpenQuestion}
      />
      {isSingle && firstRelationship && (
        <SingleActions
          rel={firstRelationship}
          onEdit={onEdit}
          onDelete={onDelete}
          deleting={deleting}
        />
      )}
      {!isSingle && <MultiDirectionBadge isOutgoing={group.isOutgoing} />}
    </div>
  )
}

export function RelationshipGroupRow({
  group,
  isExpanded,
  onToggle,
  onEdit,
  onDelete,
  deleting,
}: RelationshipGroupRowProps): React.ReactElement {
  const isOpenQuestion = group.status === 'open-question'
  const isSingleRelationship = group.relationships.length === 1
  const firstRelationship = group.relationships[0]
  const firstTarget = group.targetEndpoints[0]
  const containerBorder = isOpenQuestion
    ? 'border-amber-200 dark:border-amber-800 bg-amber-50/50 dark:bg-amber-950/20'
    : 'border-slate-200 dark:border-slate-700'
  const singleNoteText =
    isSingleRelationship && isOpenQuestion && firstRelationship
      ? firstRelationship.investigationNotes
      : undefined
  const showExpandedBody = !isSingleRelationship && isExpanded

  return (
    <div className={`rounded-lg border transition-colors ${containerBorder}`}>
      <GroupHeaderRow
        group={group}
        isExpanded={isExpanded}
        isSingle={isSingleRelationship}
        firstRelationship={firstRelationship}
        firstTarget={firstTarget}
        isOpenQuestion={isOpenQuestion}
        onToggle={onToggle}
        onEdit={onEdit}
        onDelete={onDelete}
        deleting={deleting}
      />
      {singleNoteText && <SingleOpenQuestionNote note={singleNoteText} />}
      {showExpandedBody && (
        <ExpandedGroupBody group={group} onEdit={onEdit} onDelete={onDelete} deleting={deleting} />
      )}
    </div>
  )
}

interface ExpandedGroupBodyProps {
  group: RelationshipGroup
  onEdit: (rel: EndpointRelationship) => void
  onDelete: (id: string) => void
  deleting: boolean
}

function ExpandedGroupBody({
  group,
  onEdit,
  onDelete,
  deleting,
}: ExpandedGroupBodyProps): React.ReactElement {
  const { t } = useTranslation('cases')
  const unknownLabel = t('infrastructure.viewAllLinks.unknown')
  return (
    <div className="border-t border-slate-200 dark:border-slate-700">
      {group.relationships.map((rel, idx) => (
        <ExpandedRow
          key={rel.id}
          rel={rel}
          targetHostname={group.targetEndpoints[idx]?.hostname ?? unknownLabel}
          isLast={idx === group.relationships.length - 1}
          onEdit={onEdit}
          onDelete={onDelete}
          deleting={deleting}
        />
      ))}
    </div>
  )
}
